import Drawer from "@mui/material/Drawer";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAIAssistant } from "./AIAssistantProvider";
import ImageSourcePicker from "../../prof/presentationTitles/ImageSourcePicker";
import {
  getPreferOwnImages,
  getPresentationImageSource,
  setPreferOwnImages,
  setPresentationImageSource,
  subscribePresentationImageSource,
} from "../../prof/presentationTitles/imageSourcePreference";
import AIChatHeader from "./AIChatHeader";
import AIChatMessages from "./AIChatMessages";
import AIChatInput from "./AIChatInput";
import AIErrorMessage from "./AIErrorMessage";
import QuizConfirmationCard from "./quiz/QuizConfirmationCard";
import QuizSuccessCard from "./quiz/QuizSuccessCard";
import PresentationDraftCard from "./quiz/PresentationDraftCard";
import MDButton from "components/MDButton";
import { AI_PANEL_WIDTH_CSS, CONVERSATION_LIMIT_MESSAGE } from "./constants";

const PANEL_BOTTOM_GAP = 24; // px — keeps input clearly above the screen edge

export default function AIAssistantPanel() {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [imageSource, setImageSource] = useState(getPresentationImageSource);
  const [preferOwnImages, setPreferOwnImagesState] = useState(getPreferOwnImages);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  useEffect(() => subscribePresentationImageSource((next) => {
    setImageSource(next);
    setPreferOwnImagesState(getPreferOwnImages());
  }), []);
  const {
    isOpen,
    close,
    messages,
    draft,
    setDraft,
    sendMessage,
    retryMessage,
    sourceAttachment,
    attachSourceFile,
    clearSourceAttachment,
    sending,
    restoring,
    error,
    clearError,
    routeContext,
    startNewConversation,
    conversationLimitReached,
    copilotUi,
    confirmCreateQuiz,
    editQuizConfiguration,
    confirmPresentationDraft,
    discardPresentationDraft,
    updatePresentationSlide,
    removePresentationSlide,
    searchPresentationLibrary,
    addPresentationLibrarySlide,
    openCreatedQuiz,
    openCreatedQuizResults,
  } = useAIAssistant();
  const showImageSources =
    routeContext?.key === "presentation_maker" || routeContext?.key === "presentations";

  const handleSend = () => {
    if (conversationLimitReached) return;
    const text = String(draft || "").trim();
    if (!text && sourceAttachment?.status !== "ready") return;
    sendMessage(text);
  };

  const handleQuickAction = (action) => {
    if (conversationLimitReached) return;
    const id = typeof action === "string" ? "" : String(action?.id || "");
    const prompt = typeof action === "string" ? action : action?.prompt || action?.label;
    if (id === "presentation_confirm") {
      confirmPresentationDraft();
      return;
    }
    if (id === "presentation_discard") {
      discardPresentationDraft();
      return;
    }
    if (id === "quiz_confirm") {
      confirmCreateQuiz();
      return;
    }
    if (id === "quiz_configure") {
      editQuizConfiguration();
      return;
    }
    if (!prompt) return;
    sendMessage(prompt);
  };

  const handleDeckAction = (action, payload) => {
    if (conversationLimitReached) return;
    if (action === "delete-slide") {
      removePresentationSlide(payload);
      return;
    }
    if (action === "save-slide") {
      updatePresentationSlide(payload?.index, payload);
      return;
    }
    if (action === "discard") {
      discardPresentationDraft();
      return;
    }
    if (action === "make-presentation") {
      confirmPresentationDraft();
    }
  };

  const handleStarterSelect = (action) => {
    if (!action || conversationLimitReached) return;
    sendMessage(action.prompt || action.label);
  };

  const dynamicOptions = (copilotUi?.optionChips || []).map((opt) => {
    const id = String(opt.id || "");
    let prompt = opt.prompt;
    if (!prompt) {
      if (id === "open_results" || id === "open-results") prompt = "__open_results__";
      else if (id === "start_quiz" || id === "create-quiz") prompt = "Create a quiz";
      else if (
        id === "my_results" ||
        id === "check-quiz" ||
        id === "refresh_results" ||
        id === "retry-results"
      ) {
        prompt = "Analyze my student quiz results again";
      } else if (id === "ask-quizzes") prompt = "Help me with my quizzes and question bank";
      else if (id === "bank:private") prompt = "Use my private question bank";
      else if (id === "use_microlab") prompt = "Use Microlab public bank";
      else if (
        id.startsWith("cat:") ||
        id.startsWith("topic:") ||
        id.startsWith("use_count:") ||
        id.startsWith("guide:") ||
        id.startsWith("presentation_remove_slide:") ||
        id.startsWith("presentation_update_slide:") ||
        id.startsWith("presentation_remove_3d:") ||
        [
          "generate_missing",
          "confirm_save_generated",
          "discard_generated",
          "open_question_bank",
          "open_copilot",
          "continue_quiz",
          "change_difficulty",
          "switch_bank",
          "presentation_confirm",
          "presentation_discard",
          "propose_presentation",
          "quiz_confirm",
          "quiz_configure",
        ].includes(id)
      ) {
        prompt = id;
      } else if (id.startsWith("nav:")) {
        prompt = opt.prompt || opt.label;
      } else {
        prompt = opt.label;
      }
    }
    return { id: opt.id, label: opt.label, prompt };
  });

  const chatSuggestions = (() => {
    if (conversationLimitReached) return null;
    const byId = new Map(dynamicOptions.map((opt) => [String(opt.id || opt.label), opt]));
    const ensure = (opt) => {
      if (!byId.has(opt.id)) byId.set(opt.id, opt);
    };
    if (copilotUi?.pendingPresentation?.slides?.length && !copilotUi?.confirmation) {
      ensure({ id: "presentation_confirm", label: "Make presentation", prompt: "presentation_confirm" });
      ensure({ id: "presentation_discard", label: "Discard", prompt: "presentation_discard" });
      ensure({ id: "start_quiz", label: "Create a quiz", prompt: "Create a quiz" });
      ensure({ id: "quiz_configure", label: "Configure the quiz", prompt: "quiz_configure" });
    }
    if (copilotUi?.confirmation) {
      ensure({ id: "quiz_confirm", label: "Create Quiz", prompt: "quiz_confirm" });
      ensure({ id: "quiz_configure", label: "Configure the quiz", prompt: "quiz_configure" });
    }
    const presentationReady = dynamicOptions.some(
      (opt) => opt.id === "nav:presentation_maker" || opt.id === "nav:presentations"
    );
    if (presentationReady && !copilotUi?.pendingPresentation?.slides?.length && !copilotUi?.confirmation) {
      ensure({ id: "start_quiz", label: "Create a quiz", prompt: "Create a quiz" });
      ensure({ id: "quiz_configure", label: "Configure the quiz", prompt: "quiz_configure" });
    }
    const list = Array.from(byId.values());
    return list.length ? list : null;
  })();

  const panelHeight = `calc(100vh - ${PANEL_BOTTOM_GAP}px)`;
  const panelHeightDvh = `calc(100dvh - ${PANEL_BOTTOM_GAP}px)`;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={close}
      variant={isMobile ? "temporary" : "persistent"}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isOpen && !isMobile ? AI_PANEL_WIDTH_CSS : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isMobile ? "100%" : AI_PANEL_WIDTH_CSS,
          maxWidth: isMobile ? "100%" : 480,
          boxSizing: "border-box",
          borderLeft: "1px solid rgba(148,163,184,0.28)",
          boxShadow: isOpen ? "-12px 0 40px rgba(15, 23, 42, 0.08)" : "none",
          position: "fixed",
          top: 0,
          right: 0,
          // Leave a clear gap above the bottom of the screen.
          bottom: PANEL_BOTTOM_GAP,
          height: panelHeight,
          maxHeight: panelHeight,
          "@supports (height: 100dvh)": {
            height: panelHeightDvh,
            maxHeight: panelHeightDvh,
          },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#ffffff",
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: isMobile ? 0 : 16,
        },
      }}
    >
      <Box
        onDragEnter={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          setDragOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          const file = event.dataTransfer?.files?.[0];
          if (file) attachSourceFile(file);
        }}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {dragOver ? (
          <Box
            sx={{
              position: "absolute",
              inset: 12,
              zIndex: 5,
              borderRadius: 3,
              border: "2px dashed #2563eb",
              bgcolor: "rgba(239,246,255,0.92)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: "#1d4ed8",
              fontWeight: 700,
              textAlign: "center",
              px: 3,
            }}
          >
            Drop a PDF, PowerPoint, text, or Word file
          </Box>
        ) : null}
        <Box sx={{ flexShrink: 0 }}>
          <AIChatHeader
            sectionLabel={routeContext.label}
            onClose={close}
            onNewConversation={startNewConversation}
            disabled={sending || restoring}
            conversationLimitReached={conversationLimitReached}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <AIChatMessages
            messages={messages}
            sending={sending}
            restoring={restoring}
            sectionLabel={routeContext.label}
            onRetry={conversationLimitReached ? undefined : retryMessage}
            onStarterSelect={handleStarterSelect}
            suggestions={chatSuggestions}
            onSuggestionSelect={handleQuickAction}
            suggestionsDisabled={sending || restoring || copilotUi?.confirming || conversationLimitReached}
            draftSlides={copilotUi?.pendingPresentation?.slides || null}
            onDeckAction={
              copilotUi?.pendingPresentation?.slides?.length ? handleDeckAction : null
            }
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#ffffff",
            borderTop: "1px solid rgba(148,163,184,0.18)",
            pb: 1.5,
          }}
        >
          {conversationLimitReached ? (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                mx: 2,
                mb: 1,
                borderRadius: 2,
                alignItems: "center",
                "& .MuiAlert-message": { fontSize: 13 },
              }}
              action={
                <MDButton
                  size="small"
                  variant="gradient"
                  color="info"
                  onClick={startNewConversation}
                  disabled={sending || restoring}
                >
                  New conversation
                </MDButton>
              }
            >
              {CONVERSATION_LIMIT_MESSAGE}
            </Alert>
          ) : (
            <AIErrorMessage message={error} onDismiss={clearError} />
          )}
          {copilotUi?.createdQuiz ? (
            <QuizSuccessCard
              quiz={copilotUi.createdQuiz}
              onOpenDetails={openCreatedQuiz}
              onOpenResults={openCreatedQuizResults}
            />
          ) : null}
          {copilotUi?.confirmation ? (
            <QuizConfirmationCard
              preview={copilotUi.confirmation}
              onConfirm={confirmCreateQuiz}
              onEdit={editQuizConfiguration}
              confirming={copilotUi.confirming || sending}
              disabled={sending || restoring || conversationLimitReached}
            />
          ) : null}
          {copilotUi?.pendingPresentation?.slides?.length && !copilotUi?.confirmation ? (
            <PresentationDraftCard
              pending={copilotUi.pendingPresentation}
              onConfirm={confirmPresentationDraft}
              onDiscard={discardPresentationDraft}
              onUpdateSlide={updatePresentationSlide}
              onRemoveSlide={removePresentationSlide}
              onSearchLibrary={searchPresentationLibrary}
              onAddLibrarySlide={addPresentationLibrarySlide}
              confirming={copilotUi.confirming || sending}
              disabled={sending || restoring || conversationLimitReached}
            />
          ) : null}
          {showImageSources ? (
            <Box sx={{ px: 2, pb: 1.25 }}>
              <Typography sx={{ mb: 0.75, fontSize: 12, fontWeight: 700, color: "#334155" }}>
                {t("pages.presentationImages.title")}
              </Typography>
              <ImageSourcePicker
                compact
                value={imageSource}
                preferOwn={preferOwnImages}
                onChange={(next) => {
                  setImageSource(next);
                  setPresentationImageSource(next);
                }}
                onPreferOwnChange={(next) => {
                  setPreferOwnImagesState(next);
                  setPreferOwnImages(next);
                }}
              />
            </Box>
          ) : null}
          <AIChatInput
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            sending={sending}
            disabled={restoring || conversationLimitReached}
            attachment={sourceAttachment}
            onAttach={attachSourceFile}
            onClearAttachment={clearSourceAttachment}
            placeholder={
              conversationLimitReached
                ? "Start a new conversation to continue…"
                : undefined
            }
          />
        </Box>
      </Box>
    </Drawer>
  );
}
